/**
 * eBay neu verbinden (Auth'n'Auth): Start auf „eBay & Live-Update“, Rückkehr von eBay auf
 * `/auth/ebay/return` bzw. `/auth/ebay/declined`. Die SPA kennt weder SessionID noch
 * Token; sie merkt sich im Tab nur, für welchen Mandanten der Vorgang läuft. Außerhalb der App-BETA eingefroren
 * (Ballast-Abbau P8); in der BETA erneuert es das Token desselben eBay-Kontos auch im bisherigen PAT (31.10.2026).
 * Nach dem Abschluss geht es zurück auf „eBay & Live-Update“ (Flori 01.11.2026): Erneuerung und Wechsel lassen
 * Live-Update aus, der Kunde schaltet es dort wieder ein.
 */
import { Alert, Button, Center, Group, List, Loader, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { EBAY_SIGNIN_HOSTS, type EbayCompleteResponse } from '../../../shared/contract.js';
import { ApiRequestError } from '../api/client.js';
import { dateTime, t } from '../texts.js';
import { clearEbayConnect, peekEbayConnect, rememberEbayConnect, useServices } from '../services.js';
import { BackButton } from './back-link.js';

/** Nur eBays Sign-in oder (Fake in dev/lokal) die eigene Rückkehrseite sind erlaubte Ziele. */
export function isAllowedSignInUrl(url: string, ownOrigin: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.origin === ownOrigin) return parsed.pathname === '/auth/ebay/return';
    return parsed.protocol === 'https:' && (EBAY_SIGNIN_HOSTS as readonly string[]).includes(parsed.hostname);
  } catch {
    return false;
  }
}

/**
 * Startet die Anmeldung bei eBay. Bis der Browser eBay lädt, bleibt der Knopf im Ladezustand (Flori 01.11.2026: sonst
 * wirkte die Wartezeit wie ein toter Klick); nur ein Fehler gibt ihn wieder frei.
 */
export function EbayConnectButton({ tenantId, firstConnect, prominent = true }: { tenantId: string; firstConnect: boolean; prominent?: boolean }) {
  const { api, redirect } = useServices();
  const [busy, setBusy] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function start() {
    setBusy(true);
    setError(undefined);
    try {
      const { signInUrl } = await api.ebayConnect(tenantId);
      if (!isAllowedSignInUrl(signInUrl, window.location.origin)) {
        setError(t('ebay.badRedirect'));
        setBusy(false);
        return;
      }
      rememberEbayConnect(tenantId);
      setRedirecting(true);
      (redirect ?? (url => window.location.assign(url)))(signInUrl);
    } catch (caught) {
      setError(caught instanceof ApiRequestError && caught.detail ? caught.detail : t('ebay.connectFailed'));
      setBusy(false);
    }
  }

  return (
    <Stack gap={4}>
      <Button variant={prominent ? 'filled' : 'outline'} loading={busy} onClick={() => void start()} w="fit-content">
        {firstConnect ? t('ebay.connectFirst') : t('ebay.connect')}
      </Button>
      <Text size="xs" c="dimmed" aria-live="polite">
        {redirecting ? t('ebay.redirecting') : t('ebay.connectHint')}
      </Text>
      {error && (
        <Text size="xs" c="red" role="alert">
          {error}
        </Text>
      )}
    </Stack>
  );
}

type Tone = 'working' | 'success' | 'question' | 'warning' | 'error';

const TONE_ICON: Record<Exclude<Tone, 'working'>, { color: string; symbol: string }> = {
  success: { color: 'green', symbol: '✓' },
  question: { color: 'yellow', symbol: '?' },
  warning: { color: 'orange', symbol: '!' },
  error: { color: 'red', symbol: '!' },
};

/** Eigene Seite außerhalb des Kontorahmens: kleine Überschrift „eBay-Verbindung“, darunter Zustand mit Symbol und Titel. */
function Frame({ tone, title, onBack, children }: { tone: Tone; title: string; onBack?: () => void; children: ReactNode }) {
  const icon = tone === 'working' ? undefined : TONE_ICON[tone];
  return (
    <Center mih="70vh" p="md">
      <Paper withBorder shadow="sm" p="xl" radius="md" w="100%" maw={560}>
        <Stack gap="md">
          {onBack && <BackButton onClick={onBack} />}
          <Text size="xs" tt="uppercase" fw={700} c="dimmed">
            {t('ebay.return.label')}
          </Text>
          <Group gap="sm" wrap="nowrap" align="center">
            {icon ? (
              <ThemeIcon color={icon.color} radius="xl" size="lg" variant="light" aria-hidden>
                <Text fw={700}>{icon.symbol}</Text>
              </ThemeIcon>
            ) : (
              <Loader size="sm" />
            )}
            <Title order={3}>{title}</Title>
          </Group>
          {children}
        </Stack>
      </Paper>
    </Center>
  );
}

type ReturnState =
  | { kind: 'working' }
  | { kind: 'no-session' }
  | { kind: 'confirm'; previous: string; next: string; rename: boolean; accountSwitch: boolean }
  | { kind: 'done'; result: Extract<EbayCompleteResponse, { result: 'connected' }> }
  | { kind: 'error'; message: string };

export function EbayReturnPage() {
  const { api } = useServices();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tenantId] = useState(() => peekEbayConnect());
  const [state, setState] = useState<ReturnState>(() => (tenantId ? { kind: 'working' } : { kind: 'no-session' }));
  const started = useRef(false);

  async function complete(confirmNewUserName?: string) {
    if (!tenantId) return;
    setState({ kind: 'working' });
    try {
      const result: EbayCompleteResponse = await api.ebayComplete(tenantId, confirmNewUserName ? { confirmNewUserName } : {});
      if (result.result === 'confirm-user-change') {
        setState({ kind: 'confirm', previous: result.previousUserName, next: result.newUserName, rename: result.rename === true,
          accountSwitch: result.accountSwitch === true });
        return;
      }
      clearEbayConnect();
      // Name im Kontowechsel oben und Zustand auf „eBay & Live-Update“ neu laden.
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      void queryClient.invalidateQueries({ queryKey: ['status', tenantId] });
      setState({ kind: 'done', result });
    } catch (caught) {
      clearEbayConnect();
      setState({ kind: 'error', message: caught instanceof ApiRequestError && caught.detail ? caught.detail : t('ebay.connectFailed') });
    }
  }

  useEffect(() => {
    // Parameter von eBay (z. B. username) nicht im Verlauf lassen.
    void navigate({ to: '/auth/ebay/return', search: {}, replace: true });
    if (tenantId && !started.current) {
      started.current = true;
      void complete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- genau einmal beim Laden
  }, []);

  const toEbay = () => void navigate(tenantId ? { to: '/t/$tenantId/ebay', params: { tenantId } } : { to: '/' });

  if (state.kind === 'working') {
    return (
      <Frame tone="working" title={t('ebay.return.workingTitle')}>
        <Text>{t('ebay.return.working')}</Text>
      </Frame>
    );
  }
  if (state.kind === 'no-session') {
    return (
      <Frame tone="warning" title={t('ebay.return.noSessionTitle')} onBack={toEbay}>
        <Text>{t('ebay.return.noSession')}</Text>
      </Frame>
    );
  }
  if (state.kind === 'error') {
    return (
      <Frame tone="error" title={t('ebay.return.errorTitle')} onBack={toEbay}>
        <Text role="alert">{state.message}</Text>
        {tenantId && (
          <>
            <Text size="sm" c="dimmed">
              {t('ebay.return.errorRetry')}
            </Text>
            <EbayConnectButton tenantId={tenantId} firstConnect={false} />
          </>
        )}
      </Frame>
    );
  }
  if (state.kind === 'confirm') {
    const values = { previous: state.previous, next: state.next };
    const cancel = async () => {
      if (tenantId) await api.ebayCancel(tenantId).catch(() => undefined);
      clearEbayConnect();
      toEbay();
    };
    const title = state.accountSwitch ? 'ebay.return.switchTitle' : state.rename ? 'ebay.return.renameTitle' : 'ebay.return.confirmTitle';
    return (
      <Frame tone={state.accountSwitch ? 'warning' : 'question'} title={t(title)}>
        {state.accountSwitch ? (
          <>
            <Text>{t('ebay.return.switchLead', values)}</Text>
            <Stack gap={6}>
              <Text fw={600} size="sm">
                {t('ebay.return.switchWhat')}
              </Text>
              <List size="sm" spacing={6}>
                <List.Item>{t('ebay.return.switch.account', values)}</List.Item>
                <List.Item>{t('ebay.return.switch.liveUpdate', values)}</List.Item>
                <List.Item>{t('ebay.return.switch.items', values)}</List.Item>
                <List.Item>{t('ebay.return.switch.kept')}</List.Item>
                <List.Item>{t('ebay.return.switch.undo')}</List.Item>
              </List>
            </Stack>
            <Text size="sm" c="dimmed">
              {t('ebay.return.switchWrong', values)}
            </Text>
          </>
        ) : state.rename ? (
          <>
            <Text>{t('ebay.return.renameText', values)}</Text>
            <Text size="sm" c="dimmed">
              {t('ebay.return.renameWrong', values)}
            </Text>
          </>
        ) : (
          <Text>{t('ebay.return.confirmText', values)}</Text>
        )}
        <Text size="sm" c="orange.8">
          {t('ebay.return.hurry')}
        </Text>
        <Group>
          <Button {...(state.accountSwitch ? { color: 'orange' } : {})} onClick={() => void complete(state.next)}>
            {t(state.accountSwitch ? 'ebay.return.switchConfirm' : state.rename ? 'ebay.return.renameConfirm' : 'ebay.return.confirm', values)}
          </Button>
          <Button variant="default" onClick={() => void cancel()}>
            {t('ebay.return.abort')}
          </Button>
        </Group>
      </Frame>
    );
  }
  return <EbayDone tenantId={tenantId!} result={state.result} onContinue={toEbay} />;
}

/** Erfolg: wer verbunden ist, wie lange, und ob Live-Update jetzt aus ist; weiter geht es auf „eBay & Live-Update“. */
function EbayDone({ tenantId, result, onContinue }: { tenantId: string; result: Extract<EbayCompleteResponse, { result: 'connected' }>; onContinue: () => void }) {
  const { api } = useServices();
  const status = useQuery({ queryKey: ['status', tenantId], queryFn: () => api.status(tenantId) });
  const user = result.ebay.userName;
  const lead = result.accountSwitched && user
    ? t('ebay.return.doneSwitched', { next: user })
    : result.renamed && user
      ? t('ebay.return.doneRenamed', { next: user })
      : result.userChanged
        ? t('ebay.return.doneChanged')
        : user ? t('ebay.return.done', { user }) : undefined;
  const liveUpdateOff = status.data?.liveUpdate.enabled === false && status.data.liveUpdate.allowed;
  return (
    <Frame tone="success" title={t('ebay.return.doneTitle')}>
      {lead && <Text>{lead}</Text>}
      {result.ebay.expiresAt && !result.ebay.expiresAtEstimated && (
        <Text size="sm" c="dimmed">
          {t('ebay.return.doneValid', { date: dateTime(result.ebay.expiresAt) })}
        </Text>
      )}
      {liveUpdateOff && <Alert color="yellow">{t('ebay.return.liveUpdateOff')}</Alert>}
      {!result.syncQueued && !result.syncPending && !result.legacyUpdated && !liveUpdateOff && (
        <Text size="sm" c="dimmed">
          {t('ebay.return.notQueued')}
        </Text>
      )}
      {result.syncPending && <ResumeSync tenantId={tenantId} />}
      <Button onClick={onContinue} w="fit-content">
        {t('ebay.return.toEbay')}
      </Button>
    </Frame>
  );
}

/** Verbindung gespeichert, Wiederaufnahme des Abgleichs offen: erneut anstoßen (Rückkehrseite und Übersicht). */
export function ResumeSync({ tenantId, canResume = true }: { tenantId: string; canResume?: boolean }) {
  const { api } = useServices();
  const queryClient = useQueryClient();
  const resume = useMutation({
    mutationFn: () => api.ebayResume(tenantId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['status', tenantId] }),
  });
  if (resume.isSuccess) {
    return <Alert color={resume.data.resumed ? 'green' : 'gray'}>{t(resume.data.resumed ? 'ebay.return.resumed' : 'ebay.return.nothingPending')}</Alert>;
  }
  return (
    <Alert color="yellow" title={t('ebay.return.pendingTitle')}>
      <Stack gap="xs">
        <Text size="sm">{t('ebay.return.pending')}</Text>
        {resume.isError && (
          <Text size="sm" c="red" role="alert">
            {resume.error instanceof ApiRequestError && resume.error.detail ? resume.error.detail : t('common.error')}
          </Text>
        )}
        {canResume ? (
          <Button size="xs" w="fit-content" loading={resume.isPending} onClick={() => resume.mutate()}>
            {t('ebay.return.resume')}
          </Button>
        ) : (
          <Text size="xs" c="dimmed">
            {t('ebay.resumeOwnerOnly')}
          </Text>
        )}
      </Stack>
    </Alert>
  );
}

export function EbayDeclinedPage() {
  const { api } = useServices();
  const navigate = useNavigate();
  const [tenantId] = useState(() => peekEbayConnect());
  useEffect(() => {
    void navigate({ to: '/auth/ebay/declined', search: {}, replace: true });
    if (tenantId) void api.ebayCancel(tenantId).catch(() => undefined);
    clearEbayConnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- genau einmal beim Laden
  }, []);
  const toEbay = () => void navigate(tenantId ? { to: '/t/$tenantId/ebay', params: { tenantId } } : { to: '/' });
  return (
    <Frame tone="warning" title={t('ebay.declinedTitle')} onBack={toEbay}>
      <Text>{t('ebay.declined')}</Text>
      {tenantId && <EbayConnectButton tenantId={tenantId} firstConnect={false} />}
    </Frame>
  );
}
